import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { detectCapabilities, supportsWebGl } from "@/runtime/capability";
import { acquireCameraStream, releaseCameraStream } from "@/runtime/cameraViewer";
import { EighthWallRuntime } from "@/runtime/EighthWallRuntime";
import { DEFAULT_PLACEMENT, type PlacementPose, type RuntimeMode, type RuntimePhase } from "@/runtime/types";
import { WebXRRuntime } from "@/runtime/WebXRRuntime";
import { reportRuntimeError, subscribeRuntimeErrors, type RuntimeErrorRecord } from "@/runtime/runtimeErrors";
import { setWebXrDomOverlayActive } from "@/runtime/webxrDomOverlay";
import { getDemo, preloadDemo } from "@/demos/registry";
import type { DemoId, QualityTier } from "@/demos/types";
import { xrStore } from "@/scene/SceneStage";
import { initialQualityTier } from "@/scene/qualityPolicy";
import { startRuntimeFlow } from "@/app/runtimeFlow";

interface ExperienceRuntimeOptions {
  activeDemoId: DemoId;
  showNotice: (message: string) => void;
  videoRef: RefObject<HTMLVideoElement | null>;
}

export interface ExperienceRuntimeState {
  webglAvailable: boolean;
  mode: RuntimeMode;
  phase: RuntimePhase;
  placement: PlacementPose | null;
  placementProgress: number;
  quality: QualityTier;
  resetEpoch: number;
  error: string | null;
  started: boolean;
  runtimeError: RuntimeErrorRecord | null;
  eighthWallRuntime: EighthWallRuntime;
  webxrRuntime: WebXRRuntime;
  getCapabilities: () => Promise<Awaited<ReturnType<typeof detectCapabilities>>>;
  setQuality: (quality: QualityTier) => void;
  setError: (error: string | null) => void;
  setPhase: (phase: RuntimePhase) => void;
  setPlacementProgress: (progress: number) => void;
  setRuntimeError: (error: RuntimeErrorRecord | null) => void;
  startExperience: () => Promise<void>;
  continueIn3D: (message?: string) => void;
  leaveWebXRForCamera: () => Promise<void>;
  handleEighthWallError: (message: string) => void;
  handleWebglContextLost: () => void;
  handlePlacement: (pose: PlacementPose) => void;
  reposition: () => void;
}

export function useExperienceRuntime({ activeDemoId, showNotice, videoRef }: ExperienceRuntimeOptions): ExperienceRuntimeState {
  const [webglAvailable] = useState(supportsWebGl);
  const [mode, setMode] = useState<RuntimeMode>("non-ar");
  const [phase, setPhase] = useState<RuntimePhase>("idle");
  const [placement, setPlacement] = useState<PlacementPose | null>(null);
  const [placementProgress, setPlacementProgress] = useState(0);
  const [quality, setQuality] = useState<QualityTier>(() => initialQualityTier());
  const [resetEpoch, setResetEpoch] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [started, setStarted] = useState(false);
  const [runtimeError, setRuntimeError] = useState<RuntimeErrorRecord | null>(null);
  const cameraStream = useRef<MediaStream | null>(null);
  const mountedRef = useRef(true);
  const cameraRequestRef = useRef(0);
  const runtimeRequestRef = useRef(0);
  const capabilityPromiseRef = useRef<ReturnType<typeof detectCapabilities> | null>(null);
  const eighthWallFallbackRef = useRef(false);
  const webxrActiveRef = useRef(false);
  const webglRecoveryRef = useRef(false);
  const [eighthWallRuntime] = useState(() => new EighthWallRuntime());
  const [webxrRuntime] = useState(() => new WebXRRuntime(xrStore));

  useEffect(() => subscribeRuntimeErrors(setRuntimeError), []);
  useEffect(() => setWebXrDomOverlayActive(mode === "webxr"), [mode]);

  const stopCamera = useCallback(() => {
    releaseCameraStream(cameraStream.current, videoRef.current);
    cameraStream.current = null;
  }, [videoRef]);

  const continueIn3D = useCallback((message?: string) => {
    cameraRequestRef.current += 1;
    stopCamera();
    setMode("non-ar");
    setPlacement(DEFAULT_PLACEMENT);
    setPlacementProgress(1);
    setPhase("placed");
    setError(null);
    setStarted(true);
    if (message) showNotice(message);
  }, [showNotice, stopCamera]);

  const startCameraViewer = useCallback(async (recoveryMessage?: string) => {
    const requestId = ++cameraRequestRef.current;
    const video = videoRef.current;
    setPhase("requesting-permission");
    try {
      const stream = await acquireCameraStream(video);
      if (!mountedRef.current || cameraRequestRef.current !== requestId) {
        releaseCameraStream(stream, video);
        return;
      }
      cameraStream.current = stream;
      setMode("camera-viewer");
      setPlacement(DEFAULT_PLACEMENT);
      setPhase("placed");
      setError(null);
      setStarted(true);
      if (recoveryMessage) showNotice(recoveryMessage);
    } catch (cause) {
      console.warn("Camera startup failed; continuing in the 3D viewer.", cause);
      stopCamera();
      if (!mountedRef.current || cameraRequestRef.current !== requestId) return;
      continueIn3D("Camera unavailable · 3D viewer active");
    }
  }, [continueIn3D, showNotice, stopCamera, videoRef]);

  const leaveWebXRForCamera = useCallback(async () => {
    if (!webxrActiveRef.current) return;
    const requestId = ++runtimeRequestRef.current;
    webxrActiveRef.current = false;
    setPhase("recovering");
    await webxrRuntime.stop("user");
    if (!mountedRef.current || runtimeRequestRef.current !== requestId) return;
    await startCameraViewer("AR tracking unavailable · camera preview active");
  }, [startCameraViewer, webxrRuntime]);

  const getCapabilities = useCallback(() => {
    capabilityPromiseRef.current ??= detectCapabilities();
    return capabilityPromiseRef.current;
  }, []);

  useEffect(() => webxrRuntime.subscribe((snapshot) => {
    if (!webxrActiveRef.current) return;
    if (snapshot.phase === "stopped") {
      webxrActiveRef.current = false;
      setMode("non-ar");
      setPlacement(DEFAULT_PLACEMENT);
      setPlacementProgress(1);
      setPhase("placed");
      showNotice("AR session ended · 3D viewer active");
      return;
    }
    setPhase(snapshot.phase);
    setPlacement(snapshot.placement);
    setPlacementProgress(snapshot.placement ? 1 : 0);
    if (snapshot.phase === "placed" && snapshot.placementMode === "fixed") showNotice("Surface not found · fixed AR placement");
  }), [showNotice, webxrRuntime]);

  const startExperience = useCallback(async () => {
    cameraRequestRef.current += 1;
    const requestId = ++runtimeRequestRef.current;
    webglRecoveryRef.current = false;
    eighthWallFallbackRef.current = false;
    await startRuntimeFlow({
      activeDemoId,
      demoTitle: getDemo(activeDemoId).title,
      requestId,
      isMounted: () => mountedRef.current,
      isCurrent: (currentRequestId) => runtimeRequestRef.current === currentRequestId,
      preloadDemo,
      getCapabilities,
      webxrRuntime,
      eighthWallRuntime,
      stopCamera,
      startCameraViewer,
      continueIn3D,
      setStarted,
      setMode,
      setPhase,
      setError,
      setPlacement,
      setPlacementProgress,
      setWebxrActive: (active) => { webxrActiveRef.current = active; },
      showNotice,
      reportError: reportRuntimeError,
    });
  }, [activeDemoId, continueIn3D, eighthWallRuntime, getCapabilities, showNotice, startCameraViewer, stopCamera, webxrRuntime]);

  const handleWebglContextLost = useCallback(() => {
    if (!mountedRef.current || webglRecoveryRef.current || (mode !== "webxr" && mode !== "eighth-wall")) return;
    webglRecoveryRef.current = true;
    ++runtimeRequestRef.current;
    setPhase("recovering");
    console.warn(`WebGL context lost during ${mode}; returning to the 3D viewer.`);

    if (mode === "webxr") {
      webxrActiveRef.current = false;
      void webxrRuntime.stop("error").finally(() => {
        if (mountedRef.current) continueIn3D("XR rendering unavailable · 3D viewer active");
      });
      return;
    }

    void eighthWallRuntime.stop("error").finally(() => {
      if (mountedRef.current) continueIn3D("AR rendering unavailable · 3D viewer active");
    });
  }, [continueIn3D, eighthWallRuntime, mode, webxrRuntime]);

  const handleEighthWallError = useCallback((message: string) => {
    if (!mountedRef.current || eighthWallFallbackRef.current) return;
    eighthWallFallbackRef.current = true;
    reportRuntimeError(new Error(message), "8th Wall runtime failed");
    setError(`${message} Falling back to the camera viewer.`);
    setPhase("recovering");
    void eighthWallRuntime.stop("error").finally(() => {
      if (mountedRef.current) void startCameraViewer();
    });
  }, [eighthWallRuntime, startCameraViewer]);

  const handlePlacement = useCallback((next: PlacementPose) => {
    if (mode === "webxr") webxrRuntime.acceptPlacement(next);
    setPlacement(next);
    setPlacementProgress(1);
    setPhase("placed");
  }, [mode, webxrRuntime]);

  const reposition = useCallback(() => {
    if (mode === "webxr") {
      void webxrRuntime.reposition();
      return;
    }
    if (mode === "eighth-wall") {
      setPlacement(null);
      setPlacementProgress(0);
      setPhase("searching-surface");
      void eighthWallRuntime.reposition();
      return;
    }
    setPlacement(DEFAULT_PLACEMENT);
    setResetEpoch((value) => value + 1);
  }, [eighthWallRuntime, mode, webxrRuntime]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cameraRequestRef.current += 1;
      runtimeRequestRef.current += 1;
      stopCamera();
      webxrActiveRef.current = false;
      void webxrRuntime.stop("unmount");
      void eighthWallRuntime.stop("unmount");
    };
  }, [eighthWallRuntime, stopCamera, webxrRuntime]);

  return {
    webglAvailable, mode, phase, placement, placementProgress, quality, resetEpoch, error, started, runtimeError,
    eighthWallRuntime, webxrRuntime, getCapabilities, setQuality, setError, setPhase, setPlacementProgress, setRuntimeError,
    startExperience, continueIn3D, leaveWebXRForCamera, handleEighthWallError, handleWebglContextLost, handlePlacement, reposition,
  };
}
